/**
 * /auth/reset-confirm?token=... — set a new password. Phase 3b of
 * ottotester#65.
 *
 * Receives the reset token in the URL query — the reset email links to
 * `<FRONTEND_URL>/auth/reset-confirm?token=<jwt>` (built by the
 * sendResetPassword callback in apps/server/src/services/auth/betterAuth.ts,
 * not Better Auth's default backend URL). The form collects the new
 * password and posts via `authClient.resetPassword({ newPassword, token })`.
 * On success, redirects to /auth/login with a one-shot success flag in
 * navigation state so login renders a "password updated" banner.
 *
 * Single password field with a reveal toggle — no "confirm password"
 * field. The reveal toggle is the typo-protection mechanism (NIST SP
 * 800-63B), consistent with every other password field in the app.
 */
import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Lock } from 'lucide-react'

import { Button, Label, PasswordInput } from '@/components/ui'
import { authClient } from '@/auth/authClient'
import { AuthLayout } from '@/auth/AuthLayout'

export function ConfirmPasswordReset() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const token = params.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Missing token = link is broken. Show a recovery prompt rather than
  // letting the user fill out a form that will fail on submit.
  if (!token) {
    return (
      <AuthLayout
        title="Reset link invalid"
        subtitle="The password-reset link is missing its token. The link may have been truncated by your email client."
      >
        <div className="flex flex-col items-center gap-4 py-2">
          <Link to="/auth/reset" className="text-sm text-primary hover:underline">
            Request a new reset link
          </Link>
        </div>
      </AuthLayout>
    )
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    const { error } = await authClient.resetPassword({ newPassword: password, token })
    setSubmitting(false)
    if (error) {
      setError(error.message ?? 'Could not reset password. The link may have expired.')
      return
    }
    navigate('/auth/login', {
      replace: true,
      state: { passwordUpdated: true },
    })
  }

  return (
    <AuthLayout
      title="Set a new password"
      subtitle="Pick something memorable. At least 8 characters."
    >
      <form onSubmit={handleSubmit} className="space-y-3" aria-label="Confirm password reset form">
        <div className="space-y-1.5">
          <Label htmlFor="password">New password</Label>
          <PasswordInput
            id="password"
            autoComplete="new-password"
            autoFocus
            required
            minLength={8}
            revealLabel="new password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <Button type="submit" disabled={submitting} className="w-full">
          <Lock className="h-4 w-4 mr-2" />
          {submitting ? 'Updating…' : 'Update password'}
        </Button>
      </form>
    </AuthLayout>
  )
}
